import { ref, computed, onUnmounted } from "vue";
import {
  taskService,
  isTaskActive,
  getActiveTask,
  closeOpenSession,
} from "../services/taskService";
import { topicService } from "../services/topicService";
import { posthogService } from "../services/posthogService";
import type { Task, WorkSession } from "../types/task";

function calculateDuration(sessions: WorkSession[]): number {
  const now = new Date().getTime();
  return sessions.reduce((total, session) => {
    if (!session.endTime) {
      // Active session - calculate from startTime to now
      return total + (now - new Date(session.startTime).getTime());
    }
    return (
      total +
      (new Date(session.endTime).getTime() -
        new Date(session.startTime).getTime())
    );
  }, 0);
}

export function useTaskManager() {
  const tasks = ref<Task[]>([]);
  let intervalId: number | null = null;

  // Computed properties for reactive state
  const currentTask = computed(() => getActiveTask(tasks.value));
  const hasActiveTask = computed(() => currentTask.value !== null);

  // Initialize tasks
  function initializeTasks(initialTasks: Task[]) {
    tasks.value = initialTasks;
    // Start timer if there's an active task
    if (hasActiveTask.value) {
      startTimer();
    }
  }

  // Start timer for active tasks
  function startTimer() {
    if (!intervalId) {
      intervalId = setInterval(updateActiveTask, 1000);
    }
  }

  // Stop timer
  function stopTimer() {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  // Update active task duration (for UI display only, doesn't save)
  function updateActiveTask() {
    if (!hasActiveTask.value || !currentTask.value) return;

    const activeTask = currentTask.value;
    if (!activeTask.sessions || activeTask.sessions.length === 0) return;

    // Update duration in local state for display
    const taskIndex = tasks.value.findIndex((t) => t.id === activeTask.id);
    if (taskIndex !== -1) {
      tasks.value[taskIndex].duration = calculateDuration(activeTask.sessions);
    }
  }

  // Start a task
  function startTask(taskId: number): boolean {
    console.log(`useTaskManager: Starting task ${taskId}`);

    const task = tasks.value.find((t) => t.id === taskId);
    if (!task) {
      console.error(`useTaskManager: Task ${taskId} not found`);
      return false;
    }

    // If task is completed, set completed to false first
    if (task.completed) {
      task.completed = false;
    }

    const now = new Date().toISOString();
    const updatedTasks = [...tasks.value];

    // Find any task with open session and close it
    const activeTask = getActiveTask(updatedTasks);
    if (activeTask && activeTask.id !== taskId) {
      console.log(
        `useTaskManager: Closing active task ${activeTask.id} before starting ${taskId}`
      );
      const activeTaskIndex = updatedTasks.findIndex(
        (t) => t.id === activeTask.id
      );
      updatedTasks[activeTaskIndex] = closeOpenSession(activeTask, now);
    }

    // Add new session to target task
    const taskIndex = updatedTasks.findIndex((t) => t.id === taskId);
    const targetTask = updatedTasks[taskIndex];

    if (!targetTask.sessions) {
      targetTask.sessions = [];
    }

    targetTask.sessions.push({
      startTime: now,
    });

    // Update duration
    targetTask.duration = calculateDuration(targetTask.sessions);

    // Save to localStorage
    taskService.saveTasks(updatedTasks);
    tasks.value = updatedTasks;

    // Start timer
    startTimer();

    // Track analytics
    posthogService.trackTaskStarted(targetTask.name);

    return true;
  }

  // Stop a task
  function stopTask(
    taskId: number,
    trackInBetweenTime: boolean = false
  ): boolean {
    console.log(`useTaskManager: Stopping task ${taskId}`);

    const task = tasks.value.find((t) => t.id === taskId);
    if (!task) {
      console.error(`useTaskManager: Task ${taskId} not found`);
      return false;
    }

    if (!isTaskActive(task)) {
      console.log(`useTaskManager: Task ${taskId} is not active`);
      return false;
    }

    const now = new Date().toISOString();
    const updatedTasks = [...tasks.value];
    const taskIndex = updatedTasks.findIndex((t) => t.id === taskId);

    // Close current task's last session
    updatedTasks[taskIndex] = closeOpenSession(task, now);

    // Handle in-between time tracking
    if (trackInBetweenTime) {
      handleInBetweenTimeAfterStop(updatedTasks, now);
    }

    // Save to localStorage
    taskService.saveTasks(updatedTasks);
    tasks.value = updatedTasks;

    // Stop timer if no active tasks
    if (!getActiveTask(updatedTasks)) {
      stopTimer();
    } else {
      startTimer(); // Restart timer in case we started in-between task
    }

    return true;
  }

  // Complete a task
  function completeTask(
    taskId: number,
    trackInBetweenTime: boolean = false
  ): boolean {
    console.log(`useTaskManager: Completing task ${taskId}`);

    const task = tasks.value.find((t) => t.id === taskId);
    if (!task) {
      console.error(`useTaskManager: Task ${taskId} not found`);
      return false;
    }

    const now = new Date().toISOString();
    const updatedTasks = [...tasks.value];
    const taskIndex = updatedTasks.findIndex((t) => t.id === taskId);

    // Close task's last session
    let completedTask = closeOpenSession(task, now);

    // Set completed to true
    completedTask.completed = true;

    // Ensure all sessions have endTime (should already be the case)
    completedTask.sessions = completedTask.sessions.map((session) => {
      if (!session.endTime) {
        return { ...session, endTime: now };
      }
      return session;
    });

    updatedTasks[taskIndex] = completedTask;

    // Handle in-between time tracking
    if (trackInBetweenTime) {
      handleInBetweenTimeAfterStop(updatedTasks, now);
    }

    // Save to localStorage
    taskService.saveTasks(updatedTasks);
    tasks.value = updatedTasks;

    // Stop timer if no active tasks
    if (!getActiveTask(updatedTasks)) {
      stopTimer();
    } else {
      startTimer(); // Restart timer in case we started in-between task
    }

    return true;
  }

  // Handle in-between time after stopping/completing
  function handleInBetweenTimeAfterStop(tasks: Task[], now: string): void {
    // Find in-between task
    let inBetweenTask = tasks.find(
      (t) => t.topicId === "in-between" && !t.completed
    );

    // If in-between task exists and is active (error state), close it first
    if (inBetweenTask && isTaskActive(inBetweenTask)) {
      console.warn(
        `In-between task ${inBetweenTask.id} was active when stopping another task. Closing it first.`
      );
      const index = tasks.findIndex((t) => t.id === inBetweenTask!.id);
      tasks[index] = closeOpenSession(inBetweenTask, now);
      inBetweenTask = tasks[index];
    }

    // If in-between task exists but is NOT active, start it
    if (inBetweenTask && !isTaskActive(inBetweenTask)) {
      if (!inBetweenTask.sessions) {
        inBetweenTask.sessions = [];
      }
      inBetweenTask.sessions.push({
        startTime: now,
      });
      inBetweenTask.duration = calculateDuration(inBetweenTask.sessions);
      return;
    }

    // If no in-between task exists, create one
    if (!inBetweenTask) {
      const topic = topicService.getOrCreateTopic("In-Between");
      const newTask: Task = {
        id: Math.max(0, ...tasks.map((t) => t.id)) + 1,
        name: "In-Between Time",
        duration: 0,
        estimatedDuration: 0,
        completed: false,
        color: "hsl(200, 65%, 70%)", // Light Blue
        sessions: [
          {
            startTime: now,
          },
        ],
        topicId: topic.id,
        createdAt: new Date().toISOString(),
      };
      tasks.push(newTask);
    }
  }

  // Toggle task (start/stop)
  function toggleTask(
    taskId: number,
    trackInBetweenTime: boolean = false
  ): boolean {
    const task = tasks.value.find((t) => t.id === taskId);
    if (!task) {
      console.error(`useTaskManager: Task ${taskId} not found`);
      return false;
    }

    if (isTaskActive(task)) {
      // Task is active, stop it
      return stopTask(taskId, trackInBetweenTime);
    } else {
      // Task is not active, start it
      // If this is a work task (not in-between) and in-between tracking is enabled,
      // we need to ensure any active in-between task is stopped first
      if (trackInBetweenTime && task.topicId !== "in-between") {
        const activeInBetweenTask = tasks.value.find(
          (t) => t.topicId === "in-between" && isTaskActive(t)
        );

        if (activeInBetweenTask) {
          console.log(
            "Stopping active in-between task before starting work task"
          );
          stopTask(activeInBetweenTask.id, false);
        }
      }

      return startTask(taskId);
    }
  }

  // Stop all tasks
  function stopAllTasks(): boolean {
    const activeTask = getActiveTask(tasks.value);
    if (activeTask) {
      return stopTask(activeTask.id, false);
    }
    return true;
  }

  // Get tasks with updated state
  function getTasks(): Task[] {
    return tasks.value;
  }

  // Sync task state (reloads from localStorage)
  function syncTaskState(): void {
    const storedTasks = taskService.getTasks();
    tasks.value = storedTasks;
    if (hasActiveTask.value) {
      startTimer();
    } else {
      stopTimer();
    }
  }

  // Toggle idle time tracking
  function toggleIdleTime(enabled: boolean): void {
    const now = new Date().toISOString();
    const updatedTasks = [...tasks.value];

    if (enabled) {
      // Find or create in-between task
      let inBetweenTask = updatedTasks.find(
        (t) => t.topicId === "in-between" && !t.completed
      );

      if (!inBetweenTask) {
        const topic = topicService.getOrCreateTopic("In-Between");
        inBetweenTask = {
          id: Math.max(0, ...updatedTasks.map((t) => t.id)) + 1,
          name: "In-Between Time",
          duration: 0,
          estimatedDuration: 0,
          completed: false,
          color: "hsl(200, 65%, 70%)", // Light Blue
          sessions: [],
          topicId: topic.id,
          createdAt: new Date().toISOString(),
        };
        updatedTasks.push(inBetweenTask);
      }

      // If no task has an open session, start in-between task
      if (!getActiveTask(updatedTasks)) {
        if (!inBetweenTask.sessions) {
          inBetweenTask.sessions = [];
        }
        inBetweenTask.sessions.push({
          startTime: now,
        });
        inBetweenTask.duration = calculateDuration(inBetweenTask.sessions);
      }
    } else {
      // Find all in-between tasks and close their open sessions
      updatedTasks.forEach((task) => {
        if (task.topicId === "in-between" && isTaskActive(task)) {
          const index = updatedTasks.findIndex((t) => t.id === task.id);
          updatedTasks[index] = closeOpenSession(task, now);
        }
      });
    }

    // Save to localStorage
    taskService.saveTasks(updatedTasks);
    tasks.value = updatedTasks;

    // Update timer
    if (hasActiveTask.value) {
      startTimer();
    } else {
      stopTimer();
    }
  }

  // Cleanup on unmount
  onUnmounted(() => {
    stopTimer();
  });

  return {
    tasks,
    currentTask,
    hasActiveTask,
    initializeTasks,
    toggleTask,
    stopAllTasks,
    getTasks,
    syncTaskState,
    startTimer,
    stopTimer,
    completeTask,
    toggleIdleTime,
  };
}

